/* eslint-disable react/no-unstable-nested-components */
import BookmarksIcon from "components/financials/modern/icons/BookmarksIcon";
import ViewerListContextIcon from "components/financials/modern/icons/ViewerListContextIcon";
import { WalletIcon } from "components/financials/modern/icons/WalletIcon";
import { PrimitiveSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aspectRatio from "components/utils/aspectRatio";
import { LockIcon } from "lucide-react";
import { invariant } from "outvariant";
import { FormattedMessage } from "react-intl";
import { ITickerSymbolList } from "types/graphqlTypes";

interface SelectTickerSymbolListProps {
  value: ITickerSymbolList | null;
  onChange: (value: ITickerSymbolList | null) => void;
  disabled?: boolean;
}
export default function SelectTickerSymbolList({ value, disabled = false, onChange }: SelectTickerSymbolListProps) {
  const theme = useTheme();
  const selectedOption = AVAILABLE_LISTS.find(({ list }) => (value || "ALL") === list);
  invariant(selectedOption, `Invalid list: "%s"`, value);

  return (
    <Select
      value={value || "ALL"}
      onValueChange={(value) => {
        if (value === "ALL") {
          onChange(null);
          return;
        }
        onChange((value as ITickerSymbolList) || null);
      }}
    >
      <SelectTrigger
        hideChevronIcon={true}
        className={classNames(
          "inline-flex",
          "min-w-32 max-w-36 border border-slate-200 bg-white",
          "font-aeonik text-sm font-medium tracking-wide text-slate-600",
          "hover:ring-0 focus-visible:ring-2",
          "hover:ring-offset-1 focus-visible:ring-offset-1",
          "hover:border-transparent hover:ring-slate-100 hover:ring-offset-slate-300",
          "focus-visible:border-transparent focus-visible:ring-indigo-100 focus-visible:ring-offset-indigo-300",
          "disabled:select-none",
          "truncate",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
          "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
          "rounded-xl",
          selectedOption.triggerClassName,
        )}
      >
        <SelectValue asChild={true}>
          <div
            className={classNames(
              "pointer-events-none flex items-center justify-start space-x-3",
              selectedOption.labelClassName,
            )}
          >
            {selectedOption.icon}

            <strong className={classNames("text-xs font-bold tracking-wide")}>{selectedOption.label}</strong>
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent
        side="bottom"
        scrollAreaViewportClassName="space-y-1"
        className={classNames("min-w-36 rounded-2xl border border-slate-300 text-sm", {
          "border-slate-900 bg-slate-700 text-slate-300 shadow-neutral-950/40": theme === "dark",
          dark: theme === "dark",
        })}
      >
        {AVAILABLE_LISTS.map(({ label, icon, list, itemClassName }) => {
          return (
            <PrimitiveSelectItem disabled={disabled} key={list} value={list || "ALL"} className={itemClassName}>
              <div className="flex w-full cursor-pointer items-center justify-between gap-x-3 rounded-xl py-1 text-sm">
                <div className="flex items-center gap-x-2">
                  {icon}
                  <h4 className="inline-flex text-xs font-bold dark:text-slate-300">{label}</h4>
                </div>

                {disabled && (
                  <LockIcon
                    aria-hidden={true}
                    className="size-4 fill-gray-100 stroke-gray-500 dark:fill-slate-400/20 dark:stroke-slate-300"
                    absoluteStrokeWidth={true}
                    strokeWidth={2}
                  />
                )}
              </div>
            </PrimitiveSelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

const AVAILABLE_LISTS = [
  {
    label: <FormattedMessage defaultMessage="Todos Ativos" id="BcaO4O" />,
    list: "ALL",
    icon: (
      <ViewerListContextIcon
        className="inline-flex size-[18px]"
        {...aspectRatio({ originalWidth: 21, originalHeight: 19, desiredWidth: 16 })}
      />
    ),
    triggerClassName: classNames(
      "border-slate-800/20 bg-slate-600/10",
      "hover:ring-slate-500/10 hover:ring-offset-slate-900/30",
      "focus-visible:ring-slate-500/10 focus-visible:ring-offset-slate-900/30",
    ),
    labelClassName: "text-slate-700 dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-slate-600/10 data-[state=checked]:text-slate-700 hover:bg-sky-300/10",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
  {
    label: <FormattedMessage defaultMessage="Carteira" id="tz1iUJ" />,
    list: ITickerSymbolList.Portfolio,
    icon: (
      <WalletIcon
        className="inline-flex size-[18px]"
        {...aspectRatio({ originalWidth: 24, originalHeight: 20, desiredWidth: 16 })}
      />
    ),
    triggerClassName: classNames(
      "border-purple-800/20 bg-purple-600/10",
      "hover:ring-purple-500/10 hover:ring-offset-purple-900/30",
      "focus-visible:ring-purple-500/10 focus-visible:ring-offset-purple-900/30",
      "dark:border-purple-300/30 dark:bg-purple-400/20",
    ),
    labelClassName: "text-purple-700 dark:text-purple-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-purple-600/10 data-[state=checked]:text-purple-700 hover:bg-purple-300/10",
      "dark:data-[state=checked]:bg-purple-400/20 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-transparent",
    ),
  },
  {
    label: <FormattedMessage defaultMessage="Favoritos" id="g2IZZj" />,
    list: ITickerSymbolList.Bookmarks,
    icon: (
      <BookmarksIcon
        className="inline-flex size-[18px]"
        {...aspectRatio({ originalWidth: 20, originalHeight: 19, desiredWidth: 16 })}
      />
    ),
    triggerClassName: classNames(
      "border-orange-800/20 bg-orange-300/10",
      "hover:ring-orange-500/10 hover:ring-offset-orange-900/30",
      "focus-visible:ring-orange-500/10 focus-visible:ring-offset-orange-900/30",
      "dark:border-orange-300/30 dark:bg-orange-400/20",
    ),
    labelClassName: "text-[#f42706] dark:text-orange-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-orange-600/5 data-[state=checked]:text-[#f42706] hover:bg-orange-300/10",
      "dark:data-[state=checked]:bg-orange-400/20 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-transparent",
    ),
  },
];
